import { useMemo } from "react";

import { z } from "zod";

import instance from "..";
import { queryKeys } from "../queryKeys";
import { useRequest } from "./useRequest";

/**
 * Schema for account features response
 */
export const ZAccountFeatures = z.object({
  zendesk_chatbot: z.boolean(),
});

export type AccountFeatures = z.infer<typeof ZAccountFeatures>;

/**
 * Fetch account features from the features endpoint
 */
const fetchAccountFeatures = async (
  accountId: string,
): Promise<AccountFeatures> => {
  const res = await instance.get(`/accounts/${accountId}/capabilities`); // TODO: Rename to /features
  // Deliberately strict `.parse` (not `looseParse`): a malformed capabilities
  // payload must reject rather than pass raw data through. `useMultiAccountFeatures`
  // stores every fulfilled result and the globally mounted `useZendeskSupport`
  // reads `.zendesk_chatbot` unguarded, so a raw/null fallback would throw
  // during render. A rejected promise is excluded by `Promise.allSettled` and
  // surfaces as a normal query error instead.
  return ZAccountFeatures.parse(res.data);
};

/**
 * Hook to fetch features for a single account
 */
export const useAccountFeatures = (accountId?: string) => {
  return useRequest(
    queryKeys.accounts.features(accountId || ""),
    () => {
      if (!accountId) {
        throw new Error("accountId required");
      }
      return fetchAccountFeatures(accountId);
    },
    {
      enabled: !!accountId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

/**
 * Hook to fetch features for multiple accounts in a single batched request
 * Returns a map of account ID to features
 */
export const useMultiAccountFeatures = (accountIds: string[]) => {
  const queryKey = useMemo(
    () => queryKeys.accounts.featuresMulti(accountIds),
    [accountIds],
  );

  // Fetch all features in parallel
  const { data, isLoading, isError, error } = useRequest(
    queryKey,
    async () => {
      // Fetch all accounts in parallel
      const results = await Promise.allSettled(
        accountIds.map((id) => fetchAccountFeatures(id)),
      );

      // Build a map of account ID to features
      const featuresMap: Record<string, AccountFeatures> = {};
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          featuresMap[accountIds[index]] = result.value;
        }
      });

      return featuresMap;
    },
    {
      enabled: accountIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Return the features map, defaulting to empty map if no data yet
  return {
    featuresMap: data ?? {},
    isLoading,
    isError,
    error,
  };
};
